/** Shared beneficiary (Contact) form body — the SINGLE source of truth for
 * the field set that appears on the edit page AND both create modals, so they
 * can't drift apart again (they used to expose three different subsets).
 *
 * Renders: identity (person) → address (individual only) → legal-entity
 * (toggle + business type + company identity/address + company contact) →
 * country-scoped custom fields. The parent owns the state; this component is
 * presentational + fetches the custom-field schema by country.
 *
 * NOTE (Phase 1): the address still uses the flat single-address reuse — for a
 * legal entity the same `address_line/postal_code/city/country` fields render
 * inside the company block. Collapsing flat vs the structured `Address` model
 * is Phase 2. */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { CompanyIdentityFields } from "@/components/CompanyIdentityFields";
import { CountrySelector } from "@/components/CountrySelector";
import PhoneInput from "@/components/PhoneInput";
import type { BeneficiaryForm } from "@/components/beneficiaryForm";
import type { PartyDetails } from "@/lib/party";
import type { BusinessType } from "@/lib/types";
import { resolveFieldSchema } from "@/modules/campaigns";

const BUSINESS_TYPES: BusinessType[] = [
  "",
  "hotel",
  "restaurant",
  "office",
  "retail",
  "warehouse",
  "factory",
  "hospital",
  "school",
  "hoa",
  "syndic_co",
  "ngo",
  "public",
  "other",
];

export function BeneficiaryFormFields({
  form,
  onChange,
  customFields,
  onCustomFieldChange,
  hideAddress = false,
  hideCustomFields = false,
  campaign,
}: {
  form: BeneficiaryForm;
  onChange: (patch: Partial<BeneficiaryForm>) => void;
  customFields: Record<string, string>;
  onCustomFieldChange: (key: string, value: string) => void;
  /** When true, omit the flat address blocks (individual + company) — the
   *  caller renders the structured <ContactAddresses> manager instead, which
   *  is the source of truth. Used on the edit page. */
  hideAddress?: boolean;
  /** When true, omit the per-org custom-fields section — used by the session
   *  modals, which don't persist contact custom fields. */
  hideCustomFields?: boolean;
  /** Campaign id, when this form is rendered inside a specific campaign's
   *  beneficiary list (create/edit modals) — without it, `resolveSchema`
   *  never layers in that campaign's OWN `applies_to="contact"` fields
   *  (distinct from org/system-wide ones), so an operator-mapped
   *  campaign-scoped contact field silently never appeared here even
   *  though it's stored on the Contact and shows on its detail page.
   *  Omitted on the standalone ContactEditPage, which has no single
   *  campaign to scope to (a contact can belong to several). */
  campaign?: string;
}) {
  const { t } = useTranslation();
  const country = (form.country || "").toUpperCase();
  const schema = useQuery({
    queryKey: ["fields-schema", "contact", country, campaign ?? ""],
    queryFn: () => resolveFieldSchema({ applies_to: "contact", country, campaign }),
    enabled: !hideCustomFields,
  });
  const cfRows = useMemo(() => schema.data ?? [], [schema.data]);

  const party: PartyDetails = {
    legal_name: form.legal_name,
    registration_number: form.registration_number,
    vat_id: form.vat_id,
    address_line: form.address_line,
    postal_code: form.postal_code,
    city: form.city,
    country: form.country,
  };

  return (
    <div className="space-y-4">
      {/* Identity */}
      <section className="card space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {form.is_business
            ? t("contacts.contactPersonHeading", "Contact person")
            : t("contacts.identityHeading")}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>
              {t("contacts.col.firstName")}
              {/* A legal entity's identity can rest on the company name
                  alone (see canSubmitBeneficiary) — don't mark the
                  contact-person name required when there may be none. */}
              {!form.is_business && (
                <span className="ml-0.5 text-cvp-danger">*</span>
              )}
            </span>
            <input
              className="input w-full"
              value={form.first_name}
              onChange={(e) => onChange({ first_name: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>
              {t("contacts.col.lastName")}
              {!form.is_business && (
                <span className="ml-0.5 text-cvp-danger">*</span>
              )}
            </span>
            <input
              className="input w-full"
              value={form.last_name}
              onChange={(e) => onChange({ last_name: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>{t("contacts.col.email")}</span>
            <input
              type="email"
              className="input w-full"
              value={form.email}
              onChange={(e) => onChange({ email: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs text-nb-text-2">
            <span>{t("contacts.col.phone")}</span>
            <PhoneInput value={form.phone} onChange={(v) => onChange({ phone: v })} />
          </label>
        </div>
        <p className="text-xs text-nb-text-3">{t("contacts.contactRequirement")}</p>
      </section>

      {/* Individual address (a legal entity's address lives in the company block). */}
      {!form.is_business && !hideAddress && (
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("contacts.addressHeading", "Address")}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-xs text-nb-text-2 sm:col-span-2">
              <span>{t("party.addressLine", "Address")}</span>
              <input
                className="input w-full"
                value={form.address_line}
                onChange={(e) => onChange({ address_line: e.target.value })}
              />
            </label>
            <label className="space-y-1 text-xs text-nb-text-2">
              <span>{t("party.postalCode", "Postal code")}</span>
              <input
                className="input w-full"
                value={form.postal_code}
                onChange={(e) => onChange({ postal_code: e.target.value })}
              />
            </label>
            <label className="space-y-1 text-xs text-nb-text-2">
              <span>{t("contacts.col.city")}</span>
              <input
                className="input w-full"
                value={form.city}
                onChange={(e) => onChange({ city: e.target.value })}
              />
            </label>
            <div className="space-y-1 text-xs text-nb-text-2">
              <span>{t("contacts.col.country")}</span>
              <CountrySelector
                value={form.country}
                onChange={(code) => onChange({ country: code })}
                allowEmpty
              />
            </div>
          </div>
        </section>
      )}

      {/* Legal entity */}
      <section className="card space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("contacts.businessHeading", "Legal entity")}
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_business}
            onChange={(e) =>
              onChange({
                is_business: e.target.checked,
                ...(e.target.checked ? {} : { business_type: "" as BusinessType }),
              })
            }
          />
          {t("contacts.isBusiness", "Is a legal entity")}
        </label>
        {form.is_business && (
          <>
            <label className="space-y-1 text-xs text-nb-text-2">
              <span>{t("contacts.businessType", "Business type")}</span>
              <select
                className="input"
                value={form.business_type}
                onChange={(e) =>
                  onChange({ business_type: e.target.value as BusinessType })
                }
              >
                {BUSINESS_TYPES.map((bt) => (
                  <option key={bt} value={bt}>
                    {bt === ""
                      ? t("contacts.businessTypeUnset", "—")
                      : t(`contacts.businessType_${bt}`, bt)}
                  </option>
                ))}
              </select>
            </label>

            {/* Company identity + the company's own address. On the edit page
                the address is captured via the structured manager, so we drop
                the flat company-address block here. */}
            <CompanyIdentityFields
              value={party}
              onChange={(patch) => onChange(patch)}
              idPrefix="company"
              showAddress={!hideAddress}
              legalNameRequired
            />

            {/* Company point-of-contact (distinct person). */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-nb-text-3">
                {t("contacts.companyContactHeading", "Company contact")}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="space-y-1 text-xs text-nb-text-2 sm:col-span-2">
                  <span>{t("contacts.companyContactName", "Contact name")}</span>
                  <input
                    className="input w-full"
                    value={form.company_contact_name}
                    onChange={(e) =>
                      onChange({ company_contact_name: e.target.value })
                    }
                  />
                </label>
                <label className="space-y-1 text-xs text-nb-text-2">
                  <span>{t("contacts.col.email")}</span>
                  <input
                    type="email"
                    className="input w-full"
                    value={form.company_contact_email}
                    onChange={(e) =>
                      onChange({ company_contact_email: e.target.value })
                    }
                  />
                </label>
                <label className="space-y-1 text-xs text-nb-text-2">
                  <span>{t("contacts.col.phone")}</span>
                  <PhoneInput
                    value={form.company_contact_phone}
                    onChange={(v) => onChange({ company_contact_phone: v })}
                  />
                </label>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Custom fields (country-scoped) */}
      {!hideCustomFields && cfRows.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("contacts.customFieldsHeading", "Custom fields")}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {cfRows.map((fd) => (
              <label key={fd.id} className="space-y-1 text-xs text-nb-text-2">
                <span>
                  {fd.label}
                  {fd.is_required && " *"}
                  {fd.is_pii && (
                    <span className="ml-1 text-[10px] uppercase text-cvp-warn">
                      PII
                    </span>
                  )}
                </span>
                <input
                  type={
                    fd.kind === "number"
                      ? "number"
                      : fd.kind === "date"
                        ? "date"
                        : "text"
                  }
                  className="input w-full"
                  value={customFields[fd.key] ?? ""}
                  placeholder={fd.regex_pattern || undefined}
                  onChange={(e) => onCustomFieldChange(fd.key, e.target.value)}
                />
                {fd.help_text && (
                  <span className="text-[10px] text-nb-text-3">{fd.help_text}</span>
                )}
              </label>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
